<script setup lang="ts">
import {
  $deleteProjectFile,
  $getProjectFileGroups,
  $getS3FileDownloadUrls,
  $uploadSelectedItemProjectFiles,
  type ProjectFileGroupRecord,
  type ProjectFilesBatchScope,
} from '@/api/fileManagement'
import { useSnackbarStore } from '@/store/snackbar'
import { getFileIcon, getFileIconColor } from '@/helpers/utils/files/getFileIcon'

type PhotoFolder = 'SCOPING_PHOTOS' | 'INSTALLATION'

interface Props {
  projectId?: number | string | null
  itemId?: number | string | null
  itemCode?: string | null
  projectType?: 1 | 5
  canAdd?: boolean
  canDelete?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  projectId: null,
  itemId: null,
  itemCode: null,
  projectType: 1,
  canAdd: true,
  canDelete: true,
})

const snackbarStore = useSnackbarStore()

const selectedPhotoType = ref<number>(0)
const photos = ref<Array<ProjectFileGroupRecord & { previewUrl?: string }>>([])
const photosLoading = ref<boolean>(false)
const uploadLoading = ref<boolean>(false)
const deletingPhotoStates = ref<Record<string, boolean>>({})
const downloadingPhotoStates = ref<Record<string, boolean>>({})
const selectedFiles = ref<File[]>([])
const showImageFullScreen = ref<boolean>(false)
const imageFullScreenUrl = ref<string | null>(null)
const brokenPreviewUrls = ref<Set<string>>(new Set())

const selectedFolder = computed<PhotoFolder>(() => selectedPhotoType.value === 0 ? 'SCOPING_PHOTOS' : 'INSTALLATION')
const selectedLabel = computed(() => selectedPhotoType.value === 0 ? 'Scoping' : 'Installation')
const selectedItemId = computed<number | null>(() => {
  const value = Number(props.itemId)
  return Number.isInteger(value) && value > 0 ? value : null
})
const hasItemCode = computed(() => !!props.itemCode?.toString().trim())

const selectedItemIdsParam = computed(() => {
  if (!selectedItemId.value) {
    return undefined
  }

  return JSON.stringify([selectedItemId.value])
})

const isImageFile = (filename: string) => /\.(png|jpe?g|heic|heif|svg|gif|webp|bmp)$/i.test(filename)

const getFilename = (file: ProjectFileGroupRecord) => file.filename || `${file.name}${file.extension || ''}`

const hasThumbnail = (file: ProjectFileGroupRecord & { previewUrl?: string }) => {
  return !!file.previewUrl && !brokenPreviewUrls.value.has(file.previewUrl)
}

const markPreviewBroken = (url?: string) => {
  if (!url) return
  brokenPreviewUrls.value = new Set([...brokenPreviewUrls.value, url])
}

const getPreviewUrl = async (file: ProjectFileGroupRecord) => {
  if (!props.projectId || !file.key) {
    return ''
  }

  const data = await $getS3FileDownloadUrls(props.projectId, {
    key: file.key,
    intent: 'preview',
    selected_item_ids: selectedItemIdsParam.value,
  })

  return data?.url ?? data?.download_url ?? ''
}

const loadPhotos = async () => {
  if (!props.projectId || !selectedItemId.value) {
    photos.value = []
    return
  }

  photosLoading.value = true
  try {
    const scopes: ProjectFilesBatchScope[] = [{
      project_id: props.projectId,
      selected_item_ids: [selectedItemId.value],
    }]

    const response = await $getProjectFileGroups(scopes, selectedFolder.value)
    const records = response.data.results || response.data.scopes?.flatMap(scope => scope.results || []) || []
    photos.value = records.map(file => ({
      ...file,
      previewUrl: file.preview_url || '',
    }))
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Failed to Load Photos',
      text: error?.response?.data?.detail || error?.message || 'Unable to fetch photos.',
    })
  } finally {
    photosLoading.value = false
  }
}

const addPhotos = async () => {
  if (!selectedItemId.value || !hasItemCode.value || !selectedFiles.value.length) {
    return
  }

  uploadLoading.value = true
  try {
    const formData = new FormData()
    formData.append('folder', selectedFolder.value)
    formData.append('campaign_type', String(props.projectType))
    formData.append('selected_item_ids', JSON.stringify([selectedItemId.value]))
    formData.append('auto_rename_to_item_code', 'true')
    for (const file of selectedFiles.value) {
      formData.append('files', file)
    }

    const response = await $uploadSelectedItemProjectFiles(formData)
    const uploadedCount = (response.data.uploaded_files || []).length + (response.data.replaced_files || []).length
    const failedCount = (response.data.not_uploaded_files || []).length

    if (uploadedCount) {
      snackbarStore.showMessage({
        color: failedCount ? 'warning' : 'success',
        icon: failedCount ? 'mdi-alert' : 'mdi-checkbox-marked-circle',
        title: failedCount ? 'Partial Upload' : 'Success',
        text: failedCount
          ? `${uploadedCount} ${selectedLabel.value.toLowerCase()} photo(s) uploaded, ${failedCount} not uploaded.`
          : `${selectedLabel.value} photo(s) added successfully!`,
      })
      selectedFiles.value = []
      await loadPhotos()
      return
    }

    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Upload Failed',
      text: response.data.not_uploaded_files?.[0]?.error || 'No files could be uploaded.',
    })
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Upload Failed',
      text: error?.response?.data?.detail || error?.message || 'Oops, something went wrong, try again',
    })
  } finally {
    uploadLoading.value = false
  }
}

const downloadPhoto = async (photo: ProjectFileGroupRecord) => {
  if (!props.projectId || !photo.key) {
    return
  }

  downloadingPhotoStates.value[photo.id] = true
  try {
    const data = await $getS3FileDownloadUrls(props.projectId, {
      key: photo.key,
      intent: 'download',
      selected_item_ids: selectedItemIdsParam.value,
    })
    const url = data?.url ?? data?.download_url
    if (!url) {
      throw new Error('No download URL returned')
    }

    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = getFilename(photo)
    anchor.rel = 'noopener noreferrer'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Download Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to download photo.',
    })
  } finally {
    downloadingPhotoStates.value[photo.id] = false
  }
}

const deletePhoto = async (photo: ProjectFileGroupRecord) => {
  if (!props.projectId || !photo.key) {
    return
  }

  deletingPhotoStates.value[photo.id] = true
  try {
    await $deleteProjectFile(props.projectId, {
      key: photo.key,
      selected_item_ids: selectedItemIdsParam.value,
    })
    await loadPhotos()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: `${selectedLabel.value} photo deleted successfully!`,
    })
  } catch (error: any) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Delete Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to delete photo.',
    })
  } finally {
    deletingPhotoStates.value[photo.id] = false
  }
}

const openPreview = async (photo: ProjectFileGroupRecord & { previewUrl?: string }) => {
  if (!isImageFile(getFilename(photo))) {
    await downloadPhoto(photo)
    return
  }

  imageFullScreenUrl.value = await getPreviewUrl(photo)
  showImageFullScreen.value = true
}

watch(selectedPhotoType, () => loadPhotos())
watch(() => [props.projectId, props.itemId], () => loadPhotos(), { immediate: true })
</script>

<template>
  <VContainer
    fluid
    class="pl-0 py-0"
    style="max-height: 100%; overflow-y: scroll;"
  >
    <ImageFullScreen
      v-model="showImageFullScreen"
      :image-url="imageFullScreenUrl"
    />

    <VRow class="mt-1">
      <VCol cols="12">
        <VSelect
          v-model="selectedPhotoType"
          :items="[{ value: 0, title: 'Scoping' }, { value: 1, title: 'Installation' }]"
        />
      </VCol>

      <VCol
        v-if="canAdd"
        cols="9"
        class="py-0 pr-0"
      >
        <VFileInput
          v-model="selectedFiles"
          :disabled="!canAdd"
          multiple
          chips
          label="Upload Photo"
        />
      </VCol>

      <VCol
        v-if="canAdd"
        cols="3"
        class="d-flex align-center justify-end py-0"
      >
        <VBtn
          class="py-1"
          :disabled="!selectedFiles.length || !canAdd || !hasItemCode || !!uploadLoading"
          @click="addPhotos"
        >
          Add
          <VProgressCircular
            v-if="uploadLoading"
            class="ml-1"
            size="x-small"
            :width="2"
            indeterminate
            color="light"
          />
        </VBtn>
      </VCol>
    </VRow>

    <div class="position-relative mt-4">
      <VOverlay
        v-model="photosLoading"
        persistent
        absolute
        contained
        class="align-center justify-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VOverlay>

      <VRow
        v-if="photos.length"
        class="ma-0"
      >
        <VCol
          v-for="photo in photos"
          :key="photo.id"
          class="file-management-photo-wrapper p-2"
          cols="12"
        >
          <div
            v-if="photo.is_archived !== true && photo.is_removed !== true"
          >
            <div 
              class="d-flex align-center flex-wrap justify-space-between"
            >
              <VBtn
                variant="text"
                size="small"
                :loading="downloadingPhotoStates[photo.id]"
                @click="downloadPhoto(photo)"
              >
                Download
              </VBtn>

              <VBtn
                v-if="canDelete"
                icon
                size="x-small"
                variant="text"
                color="error"
                :loading="deletingPhotoStates[photo.id]"
                @click="deletePhoto(photo)"
              >
                <VIcon icon="tabler-x" />
              </VBtn>
            </div>

            <div
              class="file-management-photo-container"
              @click="openPreview(photo)"
            >
              <img
                v-if="hasThumbnail(photo)"
                class="file-management-photo-img"
                :src="photo.previewUrl || ''"
                :alt="getFilename(photo)"
                @error="markPreviewBroken(photo.previewUrl)"
              />
              <div
                v-else
                class="file-management-photo-file"
              >
                <VIcon
                  :icon="getFileIcon(getFilename(photo))"
                  :color="getFileIconColor(getFilename(photo))"
                  size="42"
                />
                <span class="text-caption mt-2 text-truncate">
                  {{ getFilename(photo) }}
                </span>
              </div>
              <VIcon
                class="file-management-photo-magnify-icon"
                icon="tabler-zoom-in"
                color="white"
              />
            </div>
          </div>

        </VCol>
      </VRow>

      <VRow
        v-else
        class="mt-2"
      >
        <VCol class="text-center">
          <span>
            No {{ selectedLabel.toLowerCase() }} photos for this item
          </span>
        </VCol>
      </VRow>
    </div>
  </VContainer>
</template>

<style lang="scss">
.file-management-photo-wrapper {
  position: relative;
  text-align: center;
  border-radius: 4px;

  .file-management-photo-container {
    width: 200px;
    height: 200px;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: zoom-in;
  }

  .file-management-photo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .file-management-photo-file {
    width: 100%;
    height: 100%;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px;
  }

  .file-management-photo-magnify-icon {
    display: none;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 40px;
    pointer-events: none;
    background-color: rgb(0 0 0 / 50%);
    border-radius: 50%;
    padding: 4px;
  }

  .file-management-photo-container:hover {
    .file-management-photo-img {
      transform: scale(1.05);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 20%);
    }

    .file-management-photo-magnify-icon {
      display: block;
    }
  }
}
</style>
